import type { TFunction } from 'i18next';
import { decide, type LabResultResponse } from '@emr/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listLabResults } from '../../../api/chart.ts';
import { useGrants } from '../../../auth/sessionContext.ts';
import { Button } from '../../../components/primitives.tsx';
import type { DataTableColumn } from '../../../components/DataTable.tsx';
import { formatDateTime } from '../../../lib/dateFormat.ts';
import { ChartTable } from './ChartTable.tsx';
import { AddLabResultDialog } from './AddLabResultDialog.tsx';

/** Non-`normal` flags get a visibly distinct treatment (color + weight, both light and dark theme —
 * `text-warning`/`text-danger` are semantic tokens, not raw colors, so both themes resolve them
 * correctly) so a clinician can scan a results list for the one row that matters. `abnormalFlag` is
 * open-but-checked text (`db/schema/labs.ts`), not a database enum, so an unrecognised value falls
 * back to the plain-text treatment rather than throwing. */
function abnormalFlagClassName(flag: string): string {
  if (flag === 'critical') return 'font-semibold text-danger';
  if (flag === 'high' || flag === 'low') return 'font-medium text-warning';
  return 'text-ink';
}

function columnsFor(
  t: TFunction<'patientChartTabs'>,
): readonly DataTableColumn<LabResultResponse>[] {
  return [
    {
      key: 'test',
      header: t('labs.columns.test'),
      cell: (result) => result.testName,
      wrap: 'anywhere',
      maxWidthCh: 22,
    },
    {
      key: 'value',
      header: t('labs.columns.value'),
      cell: (result) => `${result.value} ${result.unit}`,
      wrap: 'anywhere',
      maxWidthCh: 16,
    },
    {
      key: 'range',
      header: t('labs.columns.range'),
      cell: (result) => result.referenceRange,
      wrap: 'anywhere',
      maxWidthCh: 16,
    },
    {
      key: 'flag',
      header: t('labs.columns.flag'),
      cell: (result) => (
        <span className={abnormalFlagClassName(result.abnormalFlag)}>{result.abnormalFlag}</span>
      ),
      wrap: 'wrap',
      maxWidthCh: 12,
    },
    {
      key: 'resulted',
      header: t('labs.columns.resulted'),
      cell: (result) => formatDateTime(result.resultedAt),
      wrap: 'nowrap',
    },
  ];
}

export function LabsTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {mayWrite ? (
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            {t('labs.addButton')}
          </Button>
        </div>
      ) : null}

      <ChartTable
        queryKey={['patient', patientId, 'labs']}
        queryFn={(signal) => listLabResults(patientId, signal)}
        columns={columnsFor(t)}
        rowKey={(result) => result.id}
        minWidthRem={44}
        emptyMessage={t('labs.empty')}
        loadingLabel={t('labs.loading')}
        caption={t('labs.caption')}
      />

      {mayWrite ? (
        <AddLabResultDialog
          patientId={patientId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSaved={() => undefined}
        />
      ) : null}
    </div>
  );
}
